import React from 'react';
import { Callout, Step, DocHeading, DocTable } from './components';
import { CODEX_URL, CODEX_LABEL } from '../../constants/siteInfo';

/*
 * 2026-10-07: kulcs nélkül. A vásárlás a Codex oldalán (mortisengine.com) történik, és a napok maguktól a fiókra
 * kerülnek (codex-backend services/accessGrant.js) — kulcsot sehol nem kell másolni vagy aktiválni. A visszatérítés
 * szövege a régi oldalé, a kulcs nélkül; a jogi szövegek átírása (6. lépés) után ehhez kell igazítani.
 */

function CodexLink({ path = '', children }) {
  return (
    <a href={`${CODEX_URL}${path}`} className="text-brand-400 hover:text-brand-300 underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function PageHowToBuy() {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">How to Purchase</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Access is sold on <CodexLink>{CODEX_LABEL}</CodexLink>. After payment the time is added to your account
        automatically — there is no licence key to copy or activate. This site and the loader use your own app login,
        which is linked to your {CODEX_LABEL} account — you get it through a ticket on our Discord.
      </p>

      <DocHeading>Available Payment Methods</DocHeading>
      <DocTable
        headers={['Method', 'Processor', 'Extra fee', 'Delivery time']}
        rows={[
          ['Crypto — Litecoin (LTC)', 'Direct on-chain', 'None', '~15–30 min (3 confirmations)'],
          ['Card (Visa / Mastercard)', 'Whop.com', 'None', 'Instant after payment'],
        ]}
      />
      <Callout type="info">
        Both payment methods cost exactly the same: the price shown on the pricing page is
        the price you pay. Crypto is confirmed on-chain, card payments are instant.
      </Callout>

      <DocHeading>Step 1 — Account</DocHeading>
      <Step n={1} title="Create your account">
        On <CodexLink path="/register">{CODEX_LABEL}</CodexLink>, choose a username and password and confirm your
        email address. Accounts are made there only — this site has no sign-up.
      </Step>
      <Step n={2} title="Sign in">
        Sign in on {CODEX_LABEL} with the same account.
      </Step>

      <DocHeading>Step 2 — Choose a Plan</DocHeading>
      <Step n={1} title="Open Pricing">
        Open <CodexLink path="/product">Pricing</CodexLink> on {CODEX_LABEL}. The plans are listed with their
        prices and durations.
      </Step>
      <Step n={2} title="Select payment method and click Purchase">
        Pick <strong>Crypto</strong> or <strong>Card</strong> and click the{' '}
        <strong>Purchase</strong> button to proceed.
      </Step>

      <DocHeading>Paying with Crypto (Litecoin)</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        A payment window opens with a unique Litecoin address and the exact LTC amount to send.
        The order is valid for <strong className="text-white">90 minutes</strong>.
      </p>
      <Step n={1} title="Copy the LTC address or scan the QR code">
        Send <strong className="text-white">exactly the displayed LTC amount</strong> to the
        shown address from any wallet or exchange (e.g. Binance). The QR code contains the
        plain address and is compatible with all major wallets.
      </Step>
      <Step n={2} title="Wait for confirmations">
        After your transaction is broadcast, the network needs{' '}
        <strong className="text-white">3 confirmations</strong> before the payment is accepted.
        This typically takes <strong className="text-white">15–30 minutes</strong> depending on
        network congestion. You can leave the page — the system tracks confirmations in the
        background.
      </Step>
      <Step n={3} title="The time is added automatically">
        Once 3 confirmations are reached, your order is marked as{' '}
        <strong className="text-white">Delivered</strong> and the days are added to your account.
        No action is needed from you.
      </Step>
      <Callout type="warning">
        Send the <strong>exact amount shown</strong> — not more, not less. The system matches
        incoming transactions by amount. Do not reuse the address for a second payment; always
        start a new order instead.
      </Callout>
      <Callout type="danger">
        If you do not send the payment within 90 minutes, the order expires. You can simply
        click Purchase again to generate a new order with a fresh address.
      </Callout>

      <DocHeading>Paying with Card</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Card payments are processed by <strong className="text-white">Whop.com</strong>, a trusted
        third-party payment processor — the checkout opens right on {CODEX_LABEL}.
      </p>
      <Step n={1} title="Complete the payment">
        After clicking Purchase, a secure checkout opens with your order pre-filled. Enter your
        card details and confirm.
      </Step>
      <Step n={2} title="The time is added instantly">
        After a successful payment our system is notified within seconds, and the days are added
        to your account. No waiting for confirmations.
      </Step>
      <Callout type="info">
        The card fields are hosted by Whop.com inside a secure frame — we never see or store your
        card details.
      </Callout>

      <DocHeading>Step 3 — Start Using It</DocHeading>
      <Step n={1} title="Get your app login and sign in here">
        Open a ticket on our Discord: we give you a one-time code (valid for one hour) and a link where you choose your
        app username and password. Sign in on this site with it — <strong>Dashboard → Licence &amp; loader</strong>{' '}
        shows your licence and the time left.
      </Step>
      <Step n={2} title="Download the loader">
        Download the loader on the same tab, then follow the <strong>Installation</strong> page.
      </Step>
      <Step n={3} title="Sign in to the loader">
        Use your app login in the loader too. Your account is bound to that PC (hardware ID) — if you change
        hardware later, see <strong>HWID Issues</strong>.
      </Step>
      <Callout type="info">
        If your subscription is frozen, you can still sign in here, but the loader cannot be downloaded or
        started until it is unfrozen.
      </Callout>

      <DocHeading>Refunds</DocHeading>
      <p className="text-sm text-gray-400">
        Refunds are only considered if the request is made within <strong className="text-white">14 days</strong>{' '}
        of purchase <strong className="text-white">and</strong> the software has not been downloaded or used.
        See the <CodexLink path="/terms">Terms of Service</CodexLink> for the full policy.
      </p>
    </div>
  );
}

export default PageHowToBuy;
